import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "../utils";

interface SendHtmlEmailPayload {
  to: string;
  subject: string;
  body: string;
}

interface SendHtmlEmailResponse {
  success: boolean;
  messageId: string;
}

/**
 * Hook for sending HTML emails via SES
 */
export function useSendHtmlEmail() {
  return useMutation<SendHtmlEmailResponse, Error, SendHtmlEmailPayload>({
    mutationFn: async (payload: SendHtmlEmailPayload) => {
      // Get authentication token from Supabase session
      const supabase = getSupabase();
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session?.access_token) {
        throw new Error("Missing authentication token");
      }

      const response = await fetch("/api/email/send-html", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to send email");
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast.success(`Email sent successfully! Message ID: ${data.messageId}`);
    },
    onError: (error) => {
      toast.error(`Failed to send email: ${error.message}`);
    },
  });
}
